import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../../../shared/contexts/OrgContext';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { CaptureItem } from '../types';
import { VideoHighlight } from '../../../shared/services/videoHighlightsService';
import { TacticsImage } from '../../../shared/services/tacticsImagesService';

interface AssetDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  asset: CaptureItem | null;
  highlightData?: VideoHighlight | null;
  tacticsData?: TacticsImage | null;
  onDeleteHighlight?: (highlightId: string, highlightName: string) => void;
  onDeleteTactics?: (tacticsId: string, tacticsName: string) => void;
  onEditHighlight?: (highlightId: string) => void;
}

const AssetDetailsDrawer: React.FC<AssetDetailsDrawerProps> = ({
  isOpen,
  onClose,
  asset,
  highlightData,
  tacticsData,
  onDeleteHighlight,
  onDeleteTactics,
  onEditHighlight,
}) => {
  const navigate = useNavigate();
  const { currentOrganization } = useOrg();
  const orgIdFromPath = extractOrgIdFromPath(window.location.pathname);
  const orgId = currentOrganization?.id || orgIdFromPath || 'default';
  const [copiedField, setCopiedField] = useState<string | null>(null);

  if (!isOpen || !asset) return null;

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const formatFileSize = (bytes: number | null | undefined): string => {
    if (!bytes) return 'Unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (iso?: string): string => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  };

  const isHighlight = Boolean(highlightData);
  const isTactics = Boolean(tacticsData);
  
  // Get URLs for highlights - use the same logic as replay app
  const getHighlightUrls = () => {
    if (!highlightData) return { enhancedUrl: null, outputUrl: null };
    
    // Enhanced URL (priority: enhanced_url > constructed from enhanced_storage_path)
    const enhancedUrl = highlightData.enhanced_url 
      ? highlightData.enhanced_url
      : highlightData.enhanced_storage_path
      ? `https://cdn.capture.kridah.com/${highlightData.enhanced_storage_path}`
      : null;
    
    // Output/Stitched URL (priority: output_url > constructed from output_storage_path)
    const outputUrl = highlightData.output_url
      ? highlightData.output_url
      : highlightData.output_storage_path
      ? `https://cdn.capture.kridah.com/${highlightData.output_storage_path}`
      : null;
    
    return { enhancedUrl, outputUrl };
  };

  const { enhancedUrl, outputUrl } = getHighlightUrls();
  
  // For tactics, use image_url or construct from storage path
  const tacticsUrl = tacticsData?.image_url 
    ? tacticsData.image_url
    : tacticsData?.image_storage_path
    ? `https://cdn.capture.kridah.com/${tacticsData.image_storage_path}`
    : null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isHighlight ? 'Highlight Details' : isTactics ? 'Tactics Image Details' : 'Asset Details'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">{asset.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Basic Information</h3>
            <div className="space-y-3">
              <div className="flex items-start justify-between">
                <span className="text-sm text-gray-500">Name</span>
                <span className="text-sm text-gray-900 text-right max-w-xs">{asset.name}</span>
              </div>
              {asset.description && (
                <div className="flex items-start justify-between">
                  <span className="text-sm text-gray-500">Description</span>
                  <span className="text-sm text-gray-900 text-right max-w-xs">{asset.description}</span>
                </div>
              )}
              {asset.updatedAt && (
                <div className="flex items-start justify-between">
                  <span className="text-sm text-gray-500">Updated At</span>
                  <span className="text-sm text-gray-900 text-right">{formatDate(asset.updatedAt)}</span>
                </div>
              )}
              {asset.status && (
                <div className="flex items-start justify-between">
                  <span className="text-sm text-gray-500">Status</span>
                  <span className={`text-sm px-2 py-1 rounded-full ${
                    asset.status === 'ready' 
                      ? 'bg-green-100 text-green-800' 
                      : asset.status === 'processing'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {asset.status === 'ready' ? 'Ready' : asset.status === 'processing' ? 'Processing' : 'Error'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Highlight-specific metadata */}
          {isHighlight && highlightData && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Highlight Information</h3>
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <span className="text-sm text-gray-500">Match ID</span>
                  <span className="text-sm text-gray-900 font-mono text-right">{highlightData.match_id}</span>
                </div>
                <div className="flex items-start justify-between">
                  <span className="text-sm text-gray-500">Status</span>
                  <span className={`text-sm px-2 py-1 rounded-full ${
                    ['stitched', 'completed'].includes(highlightData.status)
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {highlightData.status}
                  </span>
                </div>
                {highlightData.segments_data && Array.isArray(highlightData.segments_data) && (
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">Segments</span>
                    <span className="text-sm text-gray-900 text-right">{highlightData.segments_data.length} segments</span>
                  </div>
                )}
                {highlightData.created_at && (
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">Created At</span>
                    <span className="text-sm text-gray-900 text-right">{formatDate(highlightData.created_at)}</span>
                  </div>
                )}
                {highlightData.updated_at && (
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">Last Updated</span>
                    <span className="text-sm text-gray-900 text-right">{formatDate(highlightData.updated_at)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tactics-specific metadata */}
          {isTactics && tacticsData && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Tactics Image Information</h3>
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <span className="text-sm text-gray-500">Match ID</span>
                  <span className="text-sm text-gray-900 font-mono text-right">{tacticsData.match_id}</span>
                </div>
                <div className="flex items-start justify-between">
                  <span className="text-sm text-gray-500">Tournament ID</span>
                  <span className="text-sm text-gray-900 font-mono text-right">{tacticsData.tournament_id}</span>
                </div>
                <div className="flex items-start justify-between">
                  <span className="text-sm text-gray-500">Status</span>
                  <span className={`text-sm px-2 py-1 rounded-full ${
                    tacticsData.status === 'uploaded' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {tacticsData.status}
                  </span>
                </div>
                {tacticsData.file_size_bytes && (
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">File Size</span>
                    <span className="text-sm text-gray-900 text-right">{formatFileSize(tacticsData.file_size_bytes)}</span>
                  </div>
                )}
                {tacticsData.image_format && (
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">Format</span>
                    <span className="text-sm text-gray-900 text-right uppercase">{tacticsData.image_format}</span>
                  </div>
                )}
                {tacticsData.view_types && tacticsData.view_types.length > 0 && (
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">View Types</span>
                    <span className="text-sm text-gray-900 text-right">{tacticsData.view_types.join(', ')}</span>
                  </div>
                )}
                {tacticsData.created_at && (
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">Created At</span>
                    <span className="text-sm text-gray-900 text-right">{formatDate(tacticsData.created_at)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Output URLs - Highlights */}
          {isHighlight && (enhancedUrl || outputUrl) && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Output</h3>
              <div className="space-y-3">
                {/* Enhanced Video URL (if available) */}
                {enhancedUrl && (
                  <div>
                    <label className="text-xs text-gray-500">Enhanced Video URL</label>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-sm text-gray-900 font-mono flex-1 truncate">
                        {enhancedUrl}
                      </p>
                      <button
                        onClick={() => copyToClipboard(enhancedUrl, 'enhanced_url')}
                        className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                      >
                        {copiedField === 'enhanced_url' ? 'Copied!' : 'Copy'}
                      </button>
                      <a
                        href={enhancedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                      >
                        View
                      </a>
                    </div>
                  </div>
                )}
                {/* Base/Stitched Video URL */}
                {outputUrl && (
                  <div>
                    <label className="text-xs text-gray-500">Stitched Video URL</label>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-sm text-gray-900 font-mono flex-1 truncate">
                        {outputUrl}
                      </p>
                      <button
                        onClick={() => copyToClipboard(outputUrl, 'output_url')}
                        className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                      >
                        {copiedField === 'output_url' ? 'Copied!' : 'Copy'}
                      </button>
                      <a
                        href={outputUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                      >
                        View
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Output URL - Tactics */}
          {isTactics && tacticsUrl && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Output</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500">Image URL</label>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-sm text-gray-900 font-mono flex-1 truncate">
                      {tacticsUrl}
                    </p>
                    <button
                      onClick={() => copyToClipboard(tacticsUrl, 'tactics_url')}
                      className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                    >
                      {copiedField === 'tactics_url' ? 'Copied!' : 'Copy'}
                    </button>
                    <a
                      href={tacticsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                    >
                      View
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="pt-4 border-t border-gray-200 space-y-2">
            {/* View Full Details button for highlights */}
            {isHighlight && highlightData && (
              <button
                onClick={() => {
                  onClose();
                  navigate(`/o/${orgId}/capture/highlight/${highlightData.id}`);
                }}
                className="w-full px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                View Full Details
              </button>
            )}
            {/* Edit button for highlights */}
            {isHighlight && highlightData && onEditHighlight && (
              <button
                onClick={() => {
                  onEditHighlight(highlightData.id);
                }}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Edit Highlight
              </button>
            )}
            {/* Delete button for highlights */}
            {isHighlight && highlightData && onDeleteHighlight && (
              <button
                onClick={() => {
                  onDeleteHighlight(highlightData.id, highlightData.name);
                }}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Videos
              </button>
            )}

            {/* Delete button for tactics */}
            {isTactics && tacticsData && onDeleteTactics && (
              <button
                onClick={() => {
                  onDeleteTactics(tacticsData.id, tacticsData.name);
                }}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Image
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetDetailsDrawer;

